import { formatCents, formatCount, parseMoneyToCents, tt } from '@agos/shared'

/**
 * The coupons this customer holds — won on the wheel, won in a mini-game, or given to them personally.
 *
 * `GET /customer-auth/my-coupons` already does the hard part: it collects the codes from `wheel_spins`,
 * from winning `mini_game_plays` and from the customer's `last_personalized_coupon_code`, then decides
 * one **status** per coupon and sorts active first, newest win first inside a status.
 *
 * **That status is not re-derived here.** The server's rule has an ordering the obvious version gets
 * wrong: used-up is checked *before* expiry, *"because a coupon that ran out is more informative than
 * one that also happens to be past its date"*; and a coupon an administrator switched off is reported
 * as **expired** rather than as a state of its own, *"the customer cannot use it and there is nothing
 * they can do about it, which is exactly what expired means to them."* Recomputing that client-side
 * would be three chances to disagree with the screen the restaurant sees.
 *
 * What this file does is read the answer, and say what a coupon is worth in a way §14.7 permits —
 * `value` arrives as a JSON number and money is cents here.
 */

export type CouponStatus = 'active' | 'used' | 'expired'
export type CouponSource = 'wheel' | 'minigame' | 'personalized' | 'other'

export interface MyCoupon {
  code: string
  type: string
  /** Per cent for a percentage coupon; euros for a fixed one. Its own unit, as sent. */
  value: number
  minOrderCents: number
  status: CouponStatus
  expiresAt: string | null
  source: CouponSource
  wonAt: string | null
  productId: number | null
}

const STATUSES: readonly string[] = ['active', 'used', 'expired']
const SOURCES: readonly string[] = ['wheel', 'minigame', 'personalized', 'other']

export function myCouponsOf(payload: unknown): MyCoupon[] {
  if (!Array.isArray(payload)) return []
  return payload.flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) return []
    const row = entry as Record<string, unknown>
    const code = typeof row.code === 'string' ? row.code : ''
    if (code === '') return []
    const value = Number(row.value)
    return [
      {
        code,
        type: typeof row.type === 'string' ? row.type : '',
        value: Number.isFinite(value) ? value : 0,
        minOrderCents: parseMoneyToCents(row.minOrderAmount as string | number | null),
        // An unrecognised status is *not* treated as active — offering a coupon that will be refused
        // at the checkout is the one failure worth avoiding here.
        status: STATUSES.includes(String(row.status)) ? (row.status as CouponStatus) : 'expired',
        expiresAt: typeof row.expiresAt === 'string' && row.expiresAt !== '' ? row.expiresAt : null,
        source: SOURCES.includes(String(row.source)) ? (row.source as CouponSource) : 'other',
        wonAt: typeof row.wonAt === 'string' && row.wonAt !== '' ? row.wonAt : null,
        productId: typeof row.productId === 'number' ? row.productId : null,
      },
    ]
  })
}

/** Kept in the server's order, which is already active-first and newest-first inside a status. */
export function usable(coupons: readonly MyCoupon[]): MyCoupon[] {
  return coupons.filter((coupon) => coupon.status === 'active')
}

// ─── §9.272, owner O17 — legacy's filters, which AGOS never had ──────────────
//
// `MyCoupons.tsx:51`: `useState<"all" | "active" | "used" | "expired">("active")`, four buttons each
// carrying its count. AGOS listed every status together, so a customer with two live coupons and
// eleven spent ones read thirteen cards to find them.

export type CouponFilter = CouponStatus | 'all'

export const COUPON_FILTERS: readonly CouponFilter[] = ['active', 'used', 'expired', 'all']

export function couponFilterLabel(filter: CouponFilter): string {
  if (filter === 'active') return tt('Platné')
  if (filter === 'used') return tt('Uplatnené')
  if (filter === 'expired') return tt('Neplatné')
  return tt('Všetky')
}

export function couponsMatching(coupons: readonly MyCoupon[], filter: CouponFilter): MyCoupon[] {
  return filter === 'all' ? [...coupons] : coupons.filter((coupon) => coupon.status === filter)
}

export function couponFilterCount(coupons: readonly MyCoupon[], filter: CouponFilter): number {
  return couponsMatching(coupons, filter).length
}

/** `20 %` or `5,00 €` — what the coupon takes off, in its own unit. */
export function worthNote(coupon: MyCoupon): string {
  if (coupon.type === 'percent') return `${formatCount(coupon.value)} %`
  if (coupon.type === 'fixed') return formatCents(parseMoneyToCents(coupon.value))
  // A type this build does not know. The code is still shown, because the checkout will value it.
  return coupon.type
}

/** What has to be spent before it applies, or `''` when nothing does. */
export function minimumNote(coupon: MyCoupon): string {
  if (coupon.minOrderCents <= 0) return ''
  return `${tt('Platí od')} ${formatCents(coupon.minOrderCents)}`
}

export function statusNote(coupon: MyCoupon): string {
  if (coupon.status === 'used') return tt('Už uplatnený')
  if (coupon.status === 'expired') return tt('Neplatný')
  return ''
}

/**
 * The badge every coupon carries — §9.249, FB-409.
 *
 * `statusNote` answers `''` for a usable coupon, deliberately: it names a **problem**, and a usable
 * one has none. That leaves the field a customer actually scans for — *can I use this* — as the one
 * with nothing on screen, which is the owner's complaint in a different shape.
 *
 * So the badge is its own reader and always says something. `statusNote` is untouched; it has its own
 * meaning and its own callers.
 */
export function statusBadge(coupon: MyCoupon): string {
  if (coupon.status === 'used') return tt('Už uplatnený')
  if (coupon.status === 'expired') return tt('Neplatný')
  return tt('Platný')
}

/** Whether the badge is the good news or the bad — the tone, kept out of the markup. */
export function statusIsGood(coupon: MyCoupon): boolean {
  return coupon.status === 'active'
}

/** Where it came from, which is the part a customer actually remembers. */
export function sourceNote(coupon: MyCoupon): string {
  switch (coupon.source) {
    case 'wheel':
      return tt('Z kolesa šťastia')
    case 'minigame':
      return tt('Z hry')
    case 'personalized':
      return tt('Osobná ponuka')
    default:
      return ''
  }
}

/**
 * Said in place of the list. A customer with none has not lost anything — §9.230.
 *
 * **Legacy's whole sentence**, from `components/MyCoupons.tsx:112` rather than `MojProfil.tsx`: the
 * second half is the part that tells somebody how to get one. This carried only the first.
 *
 * **§9.272 — and its second sentence is in now.** This note used to say legacy's
 * *"Žiadne kupóny v tejto kategórii"* was unusable *"because AGOS has no coupon filters"*. That was
 * true and the conclusion drawn from it was backwards: the missing thing was the filters, which is
 * what the owner then asked for. The screen has categories, so the sentence about one is right when a
 * category is empty and the customer does hold coupons.
 */
export function emptyNote(hasAny = false): string {
  return hasAny
    ? tt('Žiadne kupóny v tejto kategórii')
    : tt('Zatiaľ nemáte žiadne kupóny. Hrajte mini-hry alebo zatočte kolesom!')
}
